import { useRef, useState, useEffect } from 'react'

import { Table } from './table'
import { mapFromRawData } from './table-mapping'

import { ControlBar } from '../control-bar'
import { ChartLoader } from '../chart-loader'
import { blockDataService } from '../block-data.service'
import { ChildBlockModal } from '../child-block-modal'
import { reportLayoutService } from '../report-layout.service'
import { ChartSizer } from '../../charts/common/chart-sizer'

export const TableBlock = function ({
  tableData, // Backwards compatible for now - remove this when we've switched over all existing table blocks
  blockKey,
  dataKey,
  report,
  strataDefinition,
  sliderDefinition,
  filterDefinition,
  parentControlBar,
  parentBlock,
  parentBlockSelections,
  childBlocks,
  cellFormats,
  includeSparkline,
  sparklineVariable,
  primaryLabel,
  secondaryLabel,
  columnHeadersAlign,
  columnGroups,
  columns,
  sections,
  rows,
  confidenceScaleScope,
  width = '100%',
  ...props
}) {
  const rawData = useRef()

  const [transformedData, setTransformedData] = useState()
  const [selections, setSelections] = useState()
  const [childBlockModalProps, setChildBlockModalProps] = useState({})
  const [propOverrides, setPropOverrides] = useState()

  props = reportLayoutService.applyPropOverrides(props, propOverrides)

  useEffect(() => {
    updateTable(selections)
  }, [cellFormats, columnHeadersAlign, includeSparkline, sparklineVariable, primaryLabel, secondaryLabel, columnGroups, columns, sections, rows])

  function onDataLoad (data) {
    rawData.current = data
    updateTable(selections)
  }

  function updateTable (currentSelections) {
    if (!rawData.current) {
      return
    }

    const filteredData = blockDataService.filterBlockDataByKeyValue(rawData.current, sliderDefinition && sliderDefinition.key, currentSelections.slider)
    setTransformedData(transformTableData(filteredData, { cellFormats, columnHeadersAlign, includeSparkline, sparklineVariable, primaryLabel, secondaryLabel, columnGroups, columns, sections, rows, confidenceScaleScope }))
  }

  function onControlBarChange (selections, changes) {
    if (changes.props) {
      setPropOverrides(selections.props)
    }

    if (changes.slider) {
      updateTable(selections)
    } else {
      setSelections(selections)
    }
  }

  function getTableClickHandler () {
    if (childBlocks) {
      return handleTableClick
    }
  }

  function handleTableClick (cellDetails) {
    let title = cellDetails.row.label
    if (cellDetails.section) {
      title = cellDetails.section.label + ' - ' + title
    }
    const selectionsForChildBlocks = {}

    // Pull out each child's filter values from the appropriate source
    childBlocks.forEach((childBlock) => {
      selectionsForChildBlocks[childBlock.key] = { filters: {} }
      const filters = childBlock.props.parentBlock.filters || []
      filters.forEach(({ key, source }) => {
        let filterValue
        switch (source) {
          case 'row':
          case 'section':
          case 'column':
            const { key: value, label } = cellDetails[source] || {}
            if (value) {
              filterValue = {
                value,
                label
              }
            }
            break
          case 'filter':
            filterValue = selections.filters[key]
            break
        }
        if (filterValue !== undefined) {
          selectionsForChildBlocks[childBlock.key].filters[key] = filterValue
        }
      })
    })

    setChildBlockModalProps({ isOpen: true, selectionsForChildBlocks, title: title })
  }

  function handleChildBlockModalClose () {
    setChildBlockModalProps({ isOpen: false })
  }

  return tableData
    ? (
      // Backwards compatible for now - remove this when we've switched over all existing table blocks
      <ChartSizer
        heightWidthRatio={0.5}
        heightViewportRatio={0.9}
        maxHeight={400}
      >
        {({ width, height }) => (

          <Table tableData={transformTableData(tableData, { cellFormats, includeSparkline })} {...props} />

        )}
      </ChartSizer>

    )
    : (
      <div className="report-full-width">
        <ControlBar
          strataDefinition={strataDefinition}
          sliderDefinition={sliderDefinition}
          filterDefinition={filterDefinition}
          parentControlBar={parentControlBar}
          parentBlock={parentBlock}
          parentBlockSelections={parentBlockSelections}
          onChange={onControlBarChange} />
        <ChartLoader fullHeight={true} report={report} blockKey={blockKey} dataKey={dataKey} sliderDefinition={sliderDefinition} selections={selections} width={width} onDataLoad={onDataLoad}>
          <ChartSizer
            heightWidthRatio={0.5}
            heightViewportRatio={0.9}
            maxHeight={400}
          >
            {({ width, height }) => (

              <Table tableData={transformedData} {...props} width={width} style={{ margin: 0 }} onClick={getTableClickHandler()} />

            )}

          </ChartSizer>

        </ChartLoader>
        {childBlocks && <ChildBlockModal report={report} childBlocks={childBlocks} {...childBlockModalProps} onClose={handleChildBlockModalClose} />}
      </div>
    )
}

function transformTableData (tableData, { cellFormats, columnHeadersAlign, includeSparkline, sparklineVariable, primaryLabel, secondaryLabel, columnGroups, columns, sections, rows, confidenceScaleScope }) {
  // Backwards compatible for boolean option - true will pick the first variable as it did previous to the option to choose
  if (includeSparkline) {
    sparklineVariable = true
  }
  return mapFromRawData(tableData, { cellFormats, columnHeadersAlign, sparklineVariable, primaryLabel, secondaryLabel, columnGroups, columns, sections, rows, confidenceScaleScope })
}
